import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { ExternalLink, CheckCircle2, Copy, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { DisplayM, BodyText, SubsectionHeader } from "@/components/ui/typography";
import { generateFeedbackLink } from "@/utils/feedbackLinks";
import { toast } from "sonner";

interface PagePreviewStepProps {
  page: {
    title: string;
    description: string;
    slug: string;
    passcode: string | null;
  };
  companyName: string;
  onNext: () => void;
}

export const PagePreviewStep = ({ page, companyName, onNext }: PagePreviewStepProps) => {
  const feedbackUrl = generateFeedbackLink(companyName, page.slug);

  const copyPasscode = () => {
    if (page.passcode) {
      navigator.clipboard.writeText(page.passcode);
      toast.success("Passcode copied!");
    }
  };

  return (
    <div className="p-2 sm:p-4">
      {/* Header - compact */}
      <div className="text-center mb-4">
        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        </div>
        <DisplayM className="mb-1">Your feedback page is ready!</DisplayM>
        <BodyText className="text-muted-foreground text-sm">
          We created a feedback page for {companyName}.
        </BodyText>
      </div>

      {/* Page preview card - compact */}
      <div className="border rounded-lg p-4 bg-card mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Page info */}
          <div className="flex-1 min-w-0">
            <SubsectionHeader className="mb-1.5 text-base">{page.title}</SubsectionHeader>
            <BodyText className="text-muted-foreground text-xs mb-3 line-clamp-2">
              {page.description}
            </BodyText>
            <div className="bg-muted rounded-lg p-2 mb-2">
              <p className="text-[10px] text-muted-foreground mb-0.5">Your feedback page URL:</p>
              <code className="text-xs break-all text-primary">{feedbackUrl}</code>
            </div>
            
            {/* Passcode display - compact */}
            {page.passcode && (
              <div className="bg-muted rounded-lg p-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Lock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Access Code:</span>
                    <code className="font-mono font-bold text-primary text-sm">
                      {page.passcode}
                    </code>
                  </div>
                  <button
                    onClick={copyPasscode}
                    className="p-1.5 hover:bg-background rounded transition-colors"
                    aria-label="Copy passcode"
                  >
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* QR code - smaller */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg border">
              <QRCodeSVG value={feedbackUrl} size={80} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">Scan to preview</p>
          </div>
        </div>
      </div>

      {/* Preview button */}
      <SecondaryButton
        onClick={() => window.open(`${feedbackUrl}?preview=true`, '_blank')}
        className="w-full mb-3 gap-2 min-h-[44px] text-sm"
      >
        <ExternalLink className="h-4 w-4" />
        Preview Your Page
      </SecondaryButton>

      <PrimaryButton onClick={onNext} className="w-full min-h-[44px] text-sm">
        Customize Branding →
      </PrimaryButton>
    </div>
  );
};
